import { BuffType, DRCategory, AbilityId, MatchPhase, CombatEventType, ClassType, ResourceType, TargetType, } from '../types/index.js';
import { ARENA_SIZE, MOVE_SPEED, GCD_MS, ABILITIES, AUTO_ATTACKS, CLASS_STATS, DR_MULTIPLIERS, DR_RESET_MS, } from '../constants/index.js';
// === Vector Helpers ===
export function vec3Distance(a, b) {
    const dx = a.x - b.x;
    const dz = a.z - b.z;
    return Math.sqrt(dx * dx + dz * dz);
}
export function vec3Normalize(v) {
    const len = Math.sqrt(v.x * v.x + v.z * v.z);
    if (len === 0)
        return { x: 0, y: 0, z: 0 };
    return { x: v.x / len, y: 0, z: v.z / len };
}
export function vec3Add(a, b) {
    return { x: a.x + b.x, y: a.y + b.y, z: a.z + b.z };
}
export function vec3Scale(v, s) {
    return { x: v.x * s, y: v.y * s, z: v.z * s };
}
// === Line of Sight ===
export function hasLineOfSight(from, to, pillars) {
    for (const pillar of pillars) {
        if (lineIntersectsCircle(from, to, pillar.position, pillar.radius)) {
            return false;
        }
    }
    return true;
}
function lineIntersectsCircle(p1, p2, center, radius) {
    const dx = p2.x - p1.x;
    const dz = p2.z - p1.z;
    const fx = p1.x - center.x;
    const fz = p1.z - center.z;
    const a = dx * dx + dz * dz;
    const b = 2 * (fx * dx + fz * dz);
    const c = fx * fx + fz * fz - radius * radius;
    let discriminant = b * b - 4 * a * c;
    if (discriminant < 0)
        return false;
    discriminant = Math.sqrt(discriminant);
    const t1 = (-b - discriminant) / (2 * a);
    const t2 = (-b + discriminant) / (2 * a);
    return (t1 >= 0 && t1 <= 1) || (t2 >= 0 && t2 <= 1) || (t1 < 0 && t2 > 1);
}
// === Movement ===
export function movePlayer(player, direction, deltaMs, pillars) {
    if (!player.alive)
        return;
    // Can't move while casting
    if (player.castingAbility !== null)
        return;
    // Check CC that prevents movement
    if (isRooted(player) || isIncapacitated(player) || isStunned(player))
        return;
    const normalized = vec3Normalize(direction);
    let speed = getMoveSpeed(player);
    const delta = vec3Scale(normalized, speed * (deltaMs / 1000));
    const newPos = vec3Add(player.position, delta);
    // Clamp to arena bounds
    newPos.x = Math.max(-ARENA_SIZE, Math.min(ARENA_SIZE, newPos.x));
    newPos.z = Math.max(-ARENA_SIZE, Math.min(ARENA_SIZE, newPos.z));
    // Pillar collision
    for (const pillar of pillars) {
        const dist = vec3Distance(newPos, pillar.position);
        if (dist < pillar.radius + 0.5) {
            const pushDir = vec3Normalize({
                x: newPos.x - pillar.position.x,
                y: 0,
                z: newPos.z - pillar.position.z,
            });
            newPos.x = pillar.position.x + pushDir.x * (pillar.radius + 0.5);
            newPos.z = pillar.position.z + pushDir.z * (pillar.radius + 0.5);
        }
    }
    player.position = newPos;
}
function getMoveSpeed(player) {
    let speed = MOVE_SPEED;
    const slow = player.buffs.find(b => b.type === BuffType.Slow);
    if (slow) {
        speed *= (1 - slow.value / 100);
    }
    // Fear causes random movement at 80% speed
    const fear = player.buffs.find(b => b.type === BuffType.Fear);
    if (fear) {
        speed *= 0.8;
    }
    return speed;
}
export function isRooted(player) {
    return player.buffs.some(b => b.type === BuffType.Root);
}
export function isIncapacitated(player) {
    return player.buffs.some(b => b.type === BuffType.Polymorph);
}
export function isFeared(player) {
    return player.buffs.some(b => b.type === BuffType.Fear);
}
export function isSilenced(player) {
    return player.buffs.some(b => b.type === BuffType.Silence);
}
export function isStunned(player) {
    return player.buffs.some(b => b.type === BuffType.Stun);
}
export function isCCed(player) {
    return isRooted(player) || isIncapacitated(player) || isFeared(player) || isStunned(player);
}
// === Buff Processing ===
export function tickBuffs(player, deltaMs) {
    const events = [];
    for (let i = player.buffs.length - 1; i >= 0; i--) {
        const buff = player.buffs[i];
        buff.remainingMs -= deltaMs;
        // DoT tick (every 3 seconds for SW:P)
        if (buff.type === BuffType.ShadowWordPain) {
            const totalTicks = 4; // 12s / 3s
            const tickDamage = buff.value / totalTicks;
            const prevTick = Math.floor((buff.remainingMs + deltaMs) / 3000);
            const currTick = Math.floor(buff.remainingMs / 3000);
            if (currTick < prevTick && buff.remainingMs > 0) {
                player.hp = Math.max(0, player.hp - tickDamage);
                events.push({
                    type: CombatEventType.Damage,
                    sourceId: buff.sourcePlayerId,
                    targetId: player.id,
                    abilityId: AbilityId.ShadowWordPain,
                    amount: tickDamage,
                });
                if (player.hp <= 0) {
                    player.alive = false;
                    events.push({
                        type: CombatEventType.Death,
                        sourceId: buff.sourcePlayerId,
                        targetId: player.id,
                    });
                }
            }
        }
        // Polymorph restores health over its duration.
        if (buff.type === BuffType.Polymorph && buff.remainingMs > 0) {
            const healPerSecond = player.maxHp / 6;
            const healAmount = Math.min(player.maxHp - player.hp, healPerSecond * (deltaMs / 1000));
            if (healAmount > 0) {
                player.hp = Math.min(player.maxHp, player.hp + healAmount);
                events.push({
                    type: CombatEventType.Heal,
                    sourceId: buff.sourcePlayerId,
                    targetId: player.id,
                    abilityId: AbilityId.Polymorph,
                    amount: Math.round(healAmount),
                });
            }
        }
        if (buff.remainingMs <= 0) {
            events.push({
                type: CombatEventType.BuffRemoved,
                sourceId: buff.sourcePlayerId,
                targetId: player.id,
                buffType: buff.type,
            });
            player.buffs.splice(i, 1);
        }
    }
    // Tick DR trackers
    for (let i = player.drTrackers.length - 1; i >= 0; i--) {
        player.drTrackers[i].resetTimer -= deltaMs;
        if (player.drTrackers[i].resetTimer <= 0) {
            player.drTrackers.splice(i, 1);
        }
    }
    return events;
}
// === Resource Regen ===
export function tickResources(player, deltaMs) {
    if (!player.alive)
        return;
    const stats = CLASS_STATS[player.classType];
    const regen = stats.resourceRegen * (deltaMs / 1000);
    if (player.resourceType === ResourceType.Rage) {
        // Rage decays (negative regen)
        player.resource = Math.max(0, Math.min(player.maxResource, player.resource + regen));
    }
    else {
        // Mana regens
        player.resource = Math.min(player.maxResource, player.resource + regen);
    }
}
// === Cooldown Ticking ===
export function tickCooldowns(player, deltaMs) {
    if (player.globalCooldown > 0) {
        player.globalCooldown = Math.max(0, player.globalCooldown - deltaMs);
    }
    for (const abilityId of Object.keys(player.abilityCooldowns)) {
        player.abilityCooldowns[abilityId] -= deltaMs;
        if (player.abilityCooldowns[abilityId] <= 0) {
            delete player.abilityCooldowns[abilityId];
        }
    }
}
// === Cast Bar ===
export function tickCasting(player, players, deltaMs, pillars) {
    if (!player.castingAbility || !player.alive)
        return [];
    const ability = ABILITIES[player.castingAbility];
    const castTimeMs = ability.castTime * 1000;
    player.castProgress += deltaMs / castTimeMs;
    // Check if interrupted by CC or silence
    if (isCCed(player) || isSilenced(player)) {
        const events = [{
                type: CombatEventType.Interrupt,
                sourceId: player.id,
                targetId: player.id,
                abilityId: player.castingAbility,
            }];
        player.castingAbility = null;
        player.castProgress = 0;
        player.castTargetId = null;
        return events;
    }
    if (player.castProgress >= 1) {
        // Cast complete - apply ability
        const targetId = player.castTargetId;
        const target = targetId ? players[targetId] : null;
        player.castingAbility = null;
        player.castProgress = 0;
        player.castTargetId = null;
        if (target && target.alive) {
            // Check range and LoS at completion
            if (ability.range > 0) {
                const dist = vec3Distance(player.position, target.position);
                if (dist > ability.range)
                    return [];
                if (ability.targetType === TargetType.Enemy && !hasLineOfSight(player.position, target.position, pillars))
                    return [];
            }
            return applyAbility(player, target, ability.id, players, pillars);
        }
    }
    return [];
}
// === Ability Usage ===
export function canUseAbility(player, abilityId, target, players, pillars) {
    if (!player.alive)
        return 'Dead';
    const ability = ABILITIES[abilityId];
    if (!ability)
        return 'Unknown ability';
    if (ability.classType !== player.classType)
        return 'Wrong class';
    if (player.castingAbility !== null)
        return 'Already casting';
    if (player.globalCooldown > 0)
        return 'GCD active';
    if (player.abilityCooldowns[abilityId] > 0)
        return 'On cooldown';
    if (player.resource < ability.resourceCost)
        return 'Not enough resource';
    // CC checks
    if (isIncapacitated(player))
        return 'Incapacitated';
    if (isFeared(player))
        return 'Feared';
    if (isStunned(player))
        return 'Stunned';
    if (isSilenced(player) && player.classType !== ClassType.Warrior)
        return 'Silenced';
    // Divine Shield prevents attacking
    if (ability.targetType === TargetType.Enemy && player.buffs.some(b => b.type === BuffType.DivineShield)) {
        return 'Cannot attack while invulnerable';
    }
    // Execute HP threshold check
    if (abilityId === AbilityId.Execute && target && target.hp / target.maxHp > 0.2) {
        return 'Target above 20% HP';
    }
    // Charge min range
    if (abilityId === AbilityId.Charge && target) {
        const dist = vec3Distance(player.position, target.position);
        if (dist < 8)
            return 'Too close (min 8yd)';
    }
    // Target validation
    if (ability.targetType === TargetType.Enemy) {
        if (!target)
            return 'No target';
        if (!target.alive)
            return 'Target is dead';
        if (target.team === player.team)
            return 'Target is friendly';
        if (ability.range > 0) {
            const dist = vec3Distance(player.position, target.position);
            if (dist > ability.range)
                return 'Out of range';
            if (!hasLineOfSight(player.position, target.position, pillars))
                return 'No line of sight';
        }
    }
    if (ability.targetType === TargetType.Friendly) {
        if (!target) {
            // Allow self-targeting for friendly abilities
            return null;
        }
        if (target.team !== player.team)
            return 'Target is hostile';
        if (!target.alive && abilityId !== AbilityId.Heal)
            return 'Target is dead';
        if (ability.range > 0 && target.id !== player.id) {
            const dist = vec3Distance(player.position, target.position);
            if (dist > ability.range)
                return 'Out of range';
            if (!hasLineOfSight(player.position, target.position, pillars))
                return 'No line of sight';
        }
    }
    return null;
}
export function canAutoAttack(player, target, pillars) {
    if (!player.alive)
        return 'Dead';
    if (!target)
        return 'No target';
    if (!target.alive)
        return 'Target is dead';
    if (target.team === player.team)
        return 'Target is friendly';
    if (isIncapacitated(target))
        return 'Target is polymorphed';
    if (player.castingAbility !== null)
        return 'Casting';
    if (isIncapacitated(player))
        return 'Incapacitated';
    if (isFeared(player))
        return 'Feared';
    if (isStunned(player))
        return 'Stunned';
    if (player.buffs.some(b => b.type === BuffType.DivineShield))
        return 'Invulnerable';
    const profile = AUTO_ATTACKS[player.classType];
    const distance = vec3Distance(player.position, target.position);
    if (distance > profile.range)
        return 'Out of range';
    if (profile.type === 'ranged' && !hasLineOfSight(player.position, target.position, pillars)) {
        return 'No line of sight';
    }
    return null;
}
export function startAbility(player, abilityId, target, players, pillars) {
    const ability = ABILITIES[abilityId];
    // Deduct resource
    player.resource = Math.max(0, player.resource - ability.resourceCost);
    // Set GCD
    player.globalCooldown = GCD_MS;
    // Set cooldown
    if (ability.cooldown > 0) {
        player.abilityCooldowns[abilityId] = ability.cooldown * 1000;
    }
    // Cast time ability → start casting
    if (ability.castTime > 0) {
        player.castingAbility = abilityId;
        player.castProgress = 0;
        player.castTargetId = target?.id || player.id;
        return [{
                type: CombatEventType.AbilityUsed,
                sourceId: player.id,
                targetId: target?.id || player.id,
                abilityId,
            }];
    }
    // Instant ability → apply immediately
    const events = [{
            type: CombatEventType.AbilityUsed,
            sourceId: player.id,
            targetId: target?.id || player.id,
            abilityId,
        }];
    const effectTarget = target || player;
    events.push(...applyAbility(player, effectTarget, abilityId, players, pillars));
    return events;
}
function applyAbility(source, target, abilityId, players, pillars) {
    const events = [];
    const ability = ABILITIES[abilityId];
    switch (abilityId) {
        case AbilityId.Charge: {
            // Dash to target
            const dir = vec3Normalize({
                x: target.position.x - source.position.x,
                y: 0,
                z: target.position.z - source.position.z,
            });
            source.position = vec3Add(target.position, vec3Scale(dir, -2));
            // Damage
            const dmg = applyDamage(source, target, ability.damage, events);
            // Root
            applyCC(target, BuffType.Root, ability.duration, 0, source.id, DRCategory.Root, events, players);
            // Generate rage
            generateRage(source, 15);
            break;
        }
        case AbilityId.MortalStrike: {
            const dmg = applyDamage(source, target, ability.damage, events);
            // Mortal wound debuff
            applyBuff(target, BuffType.MortalWound, ability.duration, 50, source.id, events);
            generateRage(source, 10);
            break;
        }
        case AbilityId.ShieldBlock: {
            applyBuff(source, BuffType.ShieldBlock, ability.duration, 50, source.id, events);
            break;
        }
        case AbilityId.Execute: {
            applyDamage(source, target, ability.damage, events);
            generateRage(source, 5);
            break;
        }
        case AbilityId.Hamstring: {
            applyDamage(source, target, ability.damage, events);
            applyBuff(target, BuffType.Slow, ability.duration, 50, source.id, events);
            generateRage(source, 5);
            break;
        }
        case AbilityId.Fireball: {
            applyDamage(source, target, ability.damage, events);
            break;
        }
        case AbilityId.FrostNova: {
            // AoE around caster
            for (const p of Object.values(players)) {
                if (p.team === source.team || !p.alive)
                    continue;
                const dist = vec3Distance(source.position, p.position);
                if (dist <= ability.range) {
                    applyDamage(source, p, ability.damage, events);
                    applyCC(p, BuffType.Root, ability.duration, 0, source.id, DRCategory.Root, events, players);
                }
            }
            break;
        }
        case AbilityId.Blink: {
            const rot = source.rotation;
            const forward = { x: -Math.sin(rot), y: 0, z: -Math.cos(rot) };
            const newPos = vec3Add(source.position, vec3Scale(forward, ability.range));
            newPos.x = Math.max(-ARENA_SIZE, Math.min(ARENA_SIZE, newPos.x));
            newPos.z = Math.max(-ARENA_SIZE, Math.min(ARENA_SIZE, newPos.z));
            source.position = newPos;
            // Remove roots/snares
            source.buffs = source.buffs.filter(b => b.type !== BuffType.Root && b.type !== BuffType.Slow);
            break;
        }
        case AbilityId.Polymorph: {
            applyCC(target, BuffType.Polymorph, ability.duration, 0, source.id, DRCategory.Incapacitate, events, players);
            // Poly heals the target slowly (full heal over duration)
            break;
        }
        case AbilityId.IceBarrier: {
            applyBuff(source, BuffType.IceBarrier, ability.duration, 1000, source.id, events);
            break;
        }
        case AbilityId.Counterspell: {
            if (target.castingAbility) {
                events.push({
                    type: CombatEventType.Interrupt,
                    sourceId: source.id,
                    targetId: target.id,
                    abilityId: target.castingAbility,
                });
                target.castingAbility = null;
                target.castProgress = 0;
                target.castTargetId = null;
            }
            applyBuff(target, BuffType.Silence, ability.duration, 0, source.id, events);
            break;
        }
        case AbilityId.Heal: {
            let healAmount = ability.healing;
            // Check mortal wound
            const mortalWound = target.buffs.find(b => b.type === BuffType.MortalWound);
            if (mortalWound) {
                healAmount *= 0.5;
            }
            target.hp = Math.min(target.maxHp, target.hp + healAmount);
            events.push({
                type: CombatEventType.Heal,
                sourceId: source.id,
                targetId: target.id,
                abilityId,
                amount: healAmount,
            });
            break;
        }
        case AbilityId.ShadowWordPain: {
            // Remove existing SW:P
            target.buffs = target.buffs.filter(b => b.type !== BuffType.ShadowWordPain);
            applyBuff(target, BuffType.ShadowWordPain, ability.duration, ability.damage, source.id, events);
            break;
        }
        case AbilityId.PowerWordShield: {
            applyBuff(target, BuffType.PowerWordShield, ability.duration, 800, source.id, events);
            break;
        }
        case AbilityId.Smite: {
            applyDamage(source, target, ability.damage, events);
            break;
        }
        case AbilityId.CrusaderStrike: {
            applyDamage(source, target, ability.damage, events);
            applyBuff(target, BuffType.MortalWound, ability.duration, 50, source.id, events);
            break;
        }
        case AbilityId.HammerOfJustice: {
            applyCC(target, BuffType.Stun, ability.duration, 0, source.id, DRCategory.Stun, events, players);
            break;
        }
        case AbilityId.FlashOfLight: {
            let healAmount = ability.healing;
            const mortalWound = target.buffs.find(b => b.type === BuffType.MortalWound);
            if (mortalWound)
                healAmount *= 0.5;
            target.hp = Math.min(target.maxHp, target.hp + healAmount);
            events.push({
                type: CombatEventType.Heal,
                sourceId: source.id,
                targetId: target.id,
                abilityId,
                amount: healAmount,
            });
            break;
        }
        case AbilityId.DivineShield: {
            applyBuff(source, BuffType.DivineShield, ability.duration, 0, source.id, events);
            // Cancel any incoming casts targeting the paladin
            for (const p of Object.values(players)) {
                if (p.castTargetId === source.id) {
                    p.castingAbility = null;
                    p.castProgress = 0;
                    p.castTargetId = null;
                }
            }
            break;
        }
        case AbilityId.Consecration: {
            for (const p of Object.values(players)) {
                if (p.team === source.team || !p.alive)
                    continue;
                const dist = vec3Distance(source.position, p.position);
                if (dist <= ability.range) {
                    applyDamage(source, p, ability.damage, events);
                }
            }
            break;
        }
        case AbilityId.PsychicScream: {
            for (const p of Object.values(players)) {
                if (p.team === source.team || !p.alive)
                    continue;
                const dist = vec3Distance(source.position, p.position);
                if (dist <= ability.range) {
                    applyCC(p, BuffType.Fear, ability.duration, 0, source.id, DRCategory.Fear, events, players);
                }
            }
            break;
        }
    }
    return events;
}
function applyDamage(source, target, baseDamage, events) {
    // Divine Shield: target is immune
    if (target.buffs.some(b => b.type === BuffType.DivineShield)) {
        events.push({
            type: CombatEventType.Immune,
            sourceId: source.id,
            targetId: target.id,
        });
        return 0;
    }
    let damage = baseDamage;
    // Check absorb shields
    const absorbs = target.buffs.filter(b => b.type === BuffType.IceBarrier || b.type === BuffType.PowerWordShield);
    for (const absorb of absorbs) {
        if (damage <= 0)
            break;
        const absorbed = Math.min(damage, absorb.value);
        absorb.value -= absorbed;
        damage -= absorbed;
        events.push({
            type: CombatEventType.Absorb,
            sourceId: source.id,
            targetId: target.id,
            amount: absorbed,
        });
        if (absorb.value <= 0) {
            target.buffs = target.buffs.filter(b => b !== absorb);
            events.push({
                type: CombatEventType.BuffRemoved,
                sourceId: absorb.sourcePlayerId,
                targetId: target.id,
                buffType: absorb.type,
            });
        }
    }
    // Apply armor reduction
    const armorBuff = target.buffs.find(b => b.type === BuffType.ShieldBlock);
    let armor = CLASS_STATS[target.classType].armor;
    if (armorBuff)
        armor += armorBuff.value / 100;
    armor = Math.min(0.75, armor); // cap at 75%
    damage *= (1 - armor);
    damage = Math.round(damage);
    if (damage > 0) {
        target.hp = Math.max(0, target.hp - damage);
        events.push({
            type: CombatEventType.Damage,
            sourceId: source.id,
            targetId: target.id,
            amount: damage,
        });
        // Break polymorph on damage
        const poly = target.buffs.find(b => b.type === BuffType.Polymorph);
        if (poly) {
            target.buffs = target.buffs.filter(b => b.type !== BuffType.Polymorph);
            events.push({
                type: CombatEventType.BuffRemoved,
                sourceId: poly.sourcePlayerId,
                targetId: target.id,
                buffType: BuffType.Polymorph,
            });
        }
        // Generate rage for warrior taking damage
        if (target.classType === ClassType.Warrior) {
            generateRage(target, Math.floor(damage / 50));
        }
        // Generate rage for warrior dealing damage
        if (source.classType === ClassType.Warrior) {
            generateRage(source, Math.floor(damage / 80));
        }
        if (target.hp <= 0) {
            target.alive = false;
            events.push({
                type: CombatEventType.Death,
                sourceId: source.id,
                targetId: target.id,
            });
        }
    }
    return damage;
}
function generateRage(player, amount) {
    if (player.resourceType !== ResourceType.Rage)
        return;
    player.resource = Math.min(player.maxResource, player.resource + amount);
}
function applyBuff(target, type, durationMs, value, sourceId, events) {
    // Remove existing buff of same type from same source
    target.buffs = target.buffs.filter(b => !(b.type === type && b.sourcePlayerId === sourceId));
    target.buffs.push({
        type,
        remainingMs: durationMs,
        value,
        sourcePlayerId: sourceId,
    });
    events.push({
        type: CombatEventType.BuffApplied,
        sourceId,
        targetId: target.id,
        buffType: type,
    });
}
function applyCC(target, buffType, baseDuration, value, sourceId, drCategory, events, players) {
    // Find or create DR tracker
    let tracker = target.drTrackers.find(t => t.category === drCategory);
    if (!tracker) {
        tracker = { category: drCategory, count: 0, resetTimer: DR_RESET_MS };
        target.drTrackers.push(tracker);
    }
    const multiplier = DR_MULTIPLIERS[Math.min(tracker.count, DR_MULTIPLIERS.length - 1)];
    if (multiplier === 0) {
        events.push({
            type: CombatEventType.Immune,
            sourceId,
            targetId: target.id,
            buffType,
        });
        return;
    }
    const duration = baseDuration * multiplier;
    tracker.count++;
    tracker.resetTimer = DR_RESET_MS;
    if (buffType === BuffType.Polymorph) {
        // Hard CC should not be broken by auto-attacks firing on the next tick.
        target.autoAttackTargetId = null;
        target.autoAttackCooldownMs = 0;
        if (players) {
            for (const player of Object.values(players)) {
                if (player.autoAttackTargetId === target.id) {
                    player.autoAttackTargetId = null;
                    player.autoAttackCooldownMs = 0;
                }
            }
        }
    }
    applyBuff(target, buffType, duration, value, sourceId, events);
}
function tickAutoAttacks(state, deltaMs) {
    const events = [];
    for (const player of Object.values(state.players)) {
        if (!player.alive || !player.autoAttackTargetId) {
            continue;
        }
        const target = state.players[player.autoAttackTargetId] ?? null;
        if (!target || !target.alive || target.team === player.team) {
            player.autoAttackTargetId = null;
            player.autoAttackCooldownMs = 0;
            continue;
        }
        player.autoAttackCooldownMs = Math.max(0, player.autoAttackCooldownMs - deltaMs);
        if (canAutoAttack(player, target, state.pillars) !== null || player.autoAttackCooldownMs > 0) {
            continue;
        }
        player.autoAttackCooldownMs = AUTO_ATTACKS[player.classType].intervalMs;
        applyDamage(player, target, AUTO_ATTACKS[player.classType].damage, events);
        if (!target.alive) {
            player.autoAttackTargetId = null;
        }
    }
    return events;
}
// === Game Tick ===
export function gameTick(state, deltaMs) {
    if (state.phase !== MatchPhase.Active)
        return [];
    const events = [];
    const players = Object.values(state.players);
    for (const player of players) {
        if (!player.alive)
            continue;
        // Tick buffs (DoTs, expiry)
        events.push(...tickBuffs(player, deltaMs));
        // Tick resources
        tickResources(player, deltaMs);
        // Tick cooldowns
        tickCooldowns(player, deltaMs);
        // Tick casting
        events.push(...tickCasting(player, state.players, deltaMs, state.pillars));
        // Fear random movement
        if (isFeared(player) && player.alive) {
            const fearAngle = Math.random() * Math.PI * 2;
            const fearDir = { x: Math.cos(fearAngle), y: 0, z: Math.sin(fearAngle) };
            movePlayer(player, fearDir, deltaMs, state.pillars);
        }
    }
    state.timeElapsed += deltaMs;
    events.push(...tickAutoAttacks(state, deltaMs));
    return events;
}
// === Create Player State ===
export function createPlayerState(id, name, classType, team, position) {
    const stats = CLASS_STATS[classType];
    const startingResource = classType === ClassType.Warrior
        ? Math.min(60, stats.maxResource)
        : stats.maxResource;
    return {
        id,
        name,
        classType,
        team,
        position: { ...position },
        rotation: team === 0 ? 0 : Math.PI,
        hp: stats.maxHp,
        maxHp: stats.maxHp,
        resource: startingResource,
        maxResource: stats.maxResource,
        resourceType: stats.resourceType,
        alive: true,
        buffs: [],
        drTrackers: [],
        globalCooldown: 0,
        abilityCooldowns: {},
        castingAbility: null,
        castProgress: 0,
        castTargetId: null,
        targetId: null,
        autoAttackTargetId: null,
        autoAttackCooldownMs: 0,
    };
}
//# sourceMappingURL=index.js.map